import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Types
export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

export interface Modal {
  id: string
  type: string
  isOpen: boolean
  data?: any
}

export interface UIState {
  // Global loading states
  loading: {
    global: boolean
    page: boolean
    component: Record<string, boolean>
  }
  
  // Navigation
  sidebar: {
    isOpen: boolean
    collapsed: boolean
  }
  
  // Modals
  modals: Modal[]
  
  // Toasts/Notifications
  toasts: Toast[]
  
  // Theme
  theme: 'light' | 'dark' | 'system'
  
  // Layout
  layout: {
    headerHeight: number
    sidebarWidth: number
    footerHeight: number
  }
  
  // Search
  search: {
    isOpen: boolean
    query: string
    results: any[]
    loading: boolean
  }
  
  // Preferences
  preferences: {
    language: string
    timezone: string
    currency: string
    units: 'metric' | 'imperial'
    autoplay: boolean
    notifications: boolean
  }
  
  // Device info
  device: {
    isMobile: boolean
    isTablet: boolean
    isDesktop: boolean
    touchEnabled: boolean
  }
}

const initialState: UIState = {
  loading: {
    global: false,
    page: false,
    component: {},
  },
  sidebar: {
    isOpen: false,
    collapsed: false,
  },
  modals: [],
  toasts: [],
  theme: 'system',
  layout: {
    headerHeight: 64,
    sidebarWidth: 280,
    footerHeight: 64,
  },
  search: {
    isOpen: false,
    query: '',
    results: [],
    loading: false,
  },
  preferences: {
    language: 'en',
    timezone: 'UTC',
    currency: 'USD',
    units: 'metric',
    autoplay: true,
    notifications: true,
  },
  device: {
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    touchEnabled: false,
  },
}

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Loading states
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.global = action.payload
    },
    setPageLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.page = action.payload
    },
    setComponentLoading: (state, action: PayloadAction<{ component: string; loading: boolean }>) => {
      state.loading.component[action.payload.component] = action.payload.loading
    },
    clearComponentLoading: (state, action: PayloadAction<string>) => {
      delete state.loading.component[action.payload]
    },
    
    // Sidebar
    toggleSidebar: (state) => {
      state.sidebar.isOpen = !state.sidebar.isOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebar.isOpen = action.payload
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebar.collapsed = !state.sidebar.collapsed
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebar.collapsed = action.payload
    },
    
    // Modals
    openModal: (state, action: PayloadAction<{ id: string; type: string; data?: any }>) => {
      const existingModal = state.modals.find(modal => modal.id === action.payload.id)
      if (existingModal) {
        existingModal.isOpen = true
        existingModal.data = action.payload.data
      } else {
        state.modals.push({
          id: action.payload.id,
          type: action.payload.type,
          isOpen: true,
          data: action.payload.data,
        })
      }
    },
    closeModal: (state, action: PayloadAction<string>) => {
      const modal = state.modals.find(modal => modal.id === action.payload)
      if (modal) {
        modal.isOpen = false
      }
    },
    removeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter(modal => modal.id !== action.payload)
    },
    closeAllModals: (state) => {
      state.modals.forEach(modal => {
        modal.isOpen = false
      })
    },
    
    // Toasts
    addToast: (state, action: PayloadAction<Omit<Toast, 'id'>>) => {
      const id = Date.now().toString()
      state.toasts.push({
        id,
        ...action.payload,
      })
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload)
    },
    clearToasts: (state) => {
      state.toasts = []
    },
    
    // Theme
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload
    },
    
    // Layout
    setLayoutDimensions: (state, action: PayloadAction<Partial<UIState['layout']>>) => {
      state.layout = { ...state.layout, ...action.payload }
    },
    
    // Search
    toggleSearch: (state) => {
      state.search.isOpen = !state.search.isOpen
    },
    setSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.search.isOpen = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.search.query = action.payload
    },
    setSearchResults: (state, action: PayloadAction<any[]>) => {
      state.search.results = action.payload
    },
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.search.loading = action.payload
    },
    clearSearch: (state) => {
      state.search.query = ''
      state.search.results = []
      state.search.loading = false
    },
    
    // Preferences
    setPreferences: (state, action: PayloadAction<Partial<UIState['preferences']>>) => {
      state.preferences = { ...state.preferences, ...action.payload }
    },
    
    // Device info
    setDeviceInfo: (state, action: PayloadAction<Partial<UIState['device']>>) => {
      state.device = { ...state.device, ...action.payload }
    },
  },
})

export const {
  // Loading
  setGlobalLoading,
  setPageLoading,
  setComponentLoading,
  clearComponentLoading,
  
  // Sidebar
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  setSidebarCollapsed,
  
  // Modals
  openModal,
  closeModal,
  removeModal,
  closeAllModals,
  
  // Toasts
  addToast,
  removeToast,
  clearToasts,
  
  // Theme
  setTheme,
  
  // Layout
  setLayoutDimensions,
  
  // Search
  toggleSearch,
  setSearchOpen,
  setSearchQuery,
  setSearchResults,
  setSearchLoading,
  clearSearch,
  
  // Preferences
  setPreferences,
  
  // Device
  setDeviceInfo,
} = uiSlice.actions

export default uiSlice.reducer 